import { useEffect } from 'react';

import { useJitsu } from '@jitsu/jitsu-react';

import { CommonAnalytics } from '../model/analytics';
import { APP_VERSION } from './appVersion';
import { consumeStaleChunkReload } from './staleChunk';

/**
 * Report a reload this tab made to escape a stale chunk, after the fact.
 *
 * It has to be after the fact. The reload is the whole point of that code path,
 * and `analytics.track` is an HTTP POST, so a call made on the way out is
 * cancelled by the navigation. The outcome is left in sessionStorage instead
 * and sent by whichever screen comes back -- which is also what makes it
 * evidence: a row saying `recovered = 1` can only have been written by a
 * document that loaded successfully.
 *
 * Mounted in both places a reload can land: `Root`, when the app came back, and
 * `ErrorPage`, when it came back to the same broken build. Exactly one of the
 * two renders, and `consumeStaleChunkReload` is idempotent either way. It also
 * reports nothing from a page that is itself reloading, which is where
 * `ErrorPage` usually renders.
 */
export function useReportStaleChunkReload() {
  const { analytics } = useJitsu();

  useEffect(() => {
    const outcome = consumeStaleChunkReload();

    if (!outcome) {
      return;
    }

    analytics.track(CommonAnalytics.STALE_CHUNK_RELOAD, {
      fromVersion: outcome.fromVersion,
      recovered: outcome.recovered,
      toVersion: APP_VERSION,
    });
  }, [analytics]);
}
